// Components/Services/TakeoverProjects/ProcessSection.jsx
import React from "react";
import { motion } from "framer-motion";
import { FiTool, FiCheckCircle, FiAlertCircle, FiTarget } from "react-icons/fi";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const stepVariants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const steps = [
  {
    number: "01",
    title: "Where We Step In",
    desc: "We immediately take control of your stalled or troubled construction project, providing expert leadership and project management to get things back on track. No delays, no finger-pointing — just clear ownership and a plan.",
    icon: <FiTool className="w-5 h-5 text-white" />,
  },
  {
    number: "02",
    title: "Comprehensive Evaluation",
    desc: "Our team conducts a thorough evaluation of all existing work — identifying structural issues, code violations, budget overruns, unpermitted construction, and quality defects that need immediate attention.",
    icon: <FiAlertCircle className="w-5 h-5 text-white" />,
  },
  {
    number: "03",
    title: "Take Action",
    desc: "We develop and implement strategic solutions to fix construction errors, address code violations, resolve contractor disputes, and establish proper quality control. Every correction is documented and permitted correctly.",
    icon: <FiTarget className="w-5 h-5 text-white" />,
  },
  {
    number: "04",
    title: "Finish the Job",
    desc: "We complete your project with expert craftsmanship, proper permitting, and all required final inspections — delivering a clean close-out package with warranties, lien releases, and as-built documentation.",
    icon: <FiCheckCircle className="w-5 h-5 text-white" />,
  },
];

const ProcessSection = () => {
  return (
    <section className="bg-white px-6 pt-10 pb-10 md:pt-14 md:pb-14">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-[#6c757d] uppercase tracking-wider text-xs font-semibold mb-1">
            Construction Takeover
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-[#212529]">
            How We Take Over & Finish Your Project
          </h2>
          <p className="text-[#6c757d] text-sm max-w-xl mx-auto mt-2">
            A clear, structured process — from first assessment to final handoff.
          </p>
        </motion.div>

        {/* Dark Card */}
        <motion.div
          className="bg-[#212529] rounded-2xl overflow-hidden flex flex-col md:flex-row shadow-2xl"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65 }}
        >
          {/* Left — image */}
          <div className="md:w-[36%] w-full flex-shrink-0 p-4">
            <img
              src="/Images/ADU and Custom Homes/ADU and Custom Homes 3.jpeg"
              alt="Takeover project process"
              className="w-full h-full object-cover rounded-xl"
              style={{ minHeight: "320px", maxHeight: "460px" }}
            />
          </div>

          {/* Right — steps */}
          <div className="flex-1 flex flex-col justify-between px-6 py-5 md:px-8 md:py-6">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="flex flex-col"
            >
              {steps.map((step, idx) => (
                <motion.div
                  key={idx}
                  variants={stepVariants}
                  className="flex items-center gap-4 px-3 py-2.5 rounded-xl cursor-default
                             transition-all duration-300 ease-in-out
                             hover:bg-white/[0.06] hover:scale-[1.015] hover:shadow-[0_0_0_1px_rgba(255,255,255,0.08)]
                             group"
                >
                  {/* Number */}
                  <span className="text-2xl md:text-3xl font-bold text-white/20 w-10 flex-shrink-0 leading-none
                                   group-hover:text-white/50 transition-colors duration-300">
                    {step.number}
                  </span>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold text-sm md:text-base leading-snug
                                   group-hover:text-white transition-colors duration-300">
                      {step.title}
                    </h3>
                    <p className="text-white/45 text-xs md:text-sm mt-0.5 leading-relaxed
                                  group-hover:text-white/70 transition-colors duration-300">
                      {step.desc}
                    </p>
                  </div>

                  {/* Icon */}
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center
                                  group-hover:bg-white/20 group-hover:scale-110 transition-all duration-300">
                    {step.icon}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Bottom CTA */}
            <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 border-t border-white/10">
              <p className="text-white/55 text-xs max-w-xs leading-relaxed">
                Dealing with a stalled or troubled project?{" "}
                <span className="text-white font-medium">We'll give you an honest assessment.</span>
              </p>
              <a
                href="/contact"
                className="inline-block bg-white text-[#212529] text-xs font-semibold px-5 py-2.5 rounded-full
                           hover:bg-[#f1f3f5] hover:shadow-md transition-all duration-200 whitespace-nowrap"
              >
                Get a Free Assessment
              </a>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default ProcessSection;